/* global __LW_RENDER__ */
/**
 * Render mode (spec 53 section 9.3): how scripts/training/render.sh films a
 * training module.
 *
 * The Playwright driver (e2e/training-render.spec.js) puts an object at
 * `window.__lwRender` before the app boots. When it is there, and only in a
 * development build (webpack.dev.conf.js defines __LW_RENDER__ true, the
 * production build false, so terser drops all of this), TourHost:
 *
 *   - draws nothing of its own: no card, no ring, no tag, no hint typed
 *     into an empty box, no scrim (tours/spotlight.js). The video is the
 *     bare app; Relay, laid over it afterwards, points at what the ring
 *     would have (Mark, 2026-10-27). The ring is still measured and
 *     reported in `stage` events for him;
 *   - emits the film's event stream (below), on the page's clock;
 *   - never offers "Do this step for me" on the card: the driver says when
 *     to play, after the narration has said what is about to happen;
 *   - holds a finished action step instead of moving on after its beat, so
 *     the step's `why` can be heard over the result, until the driver
 *     releases it;
 *   - hands nothing to the grader or the celebration at the end: a filmed
 *     run is not anyone's attempt.
 *
 * It hands the driver `window.__lwRender.host`: start(tourId), play(),
 * next(), release().
 *
 * THE EVENT STREAM (docs/training-video-pipeline-evaluation.md section 3).
 * Each event is a `lw:film` CustomEvent on window (the driver collects them
 * with exposeBinding; anything else can listen for the same JSON) and is
 * also kept in `window.__lwRender.events`, which the driver polls to pace
 * the film. Every event has `type`, `at` (performance.timeOrigin +
 * performance.now(), ms: the clock the screencast's frame timestamps share;
 * the driver adds `t`, ms since the film's first frame) and `step`:
 *
 *   step.start   a card is up and placed: { index, action, byHand, last }
 *                (byHand: an action step with no fallback, such as a copy to
 *                the clipboard, which the driver clicks itself)
 *   stage        where the ring and the card are, each time either moves:
 *                { ring: {x, y, w, h} | null, card: {...} | null } (CSS px, centre and size)
 *   action       the script acts on a control: { kind: click|type|select, target, label }
 *   request      the page asked the server: { id, method, path }
 *   response     the server answered: { id, status }
 *   step.done    TourHost's own check passed (the field, the element, the response)
 *   step.result  the step is finished and its result is on screen (after the
 *                render that shows it): { ok, status?, entity?, summary },
 *                or { ok: true, read: true } for a step that asks for nothing
 *   end          the run is over: { reason }
 *
 * Payloads carry ids and paths, never what was typed: a label is the
 * control's name ("Customer"), an entity is ids the step captured ({ load: 4812 }).
 */
const BUILT_IN = typeof __LW_RENDER__ !== 'undefined' && __LW_RENDER__ === true;

/** The driver's object, or null outside render mode. */
export function renderer() {
  if (!BUILT_IN || typeof window === 'undefined') return null;
  const r = window.__lwRender;
  return r && typeof r === 'object' ? r : null;
}

/** Log a film event on the page's clock. A no-op outside render mode. */
export function emit(type, detail = {}) {
  const r = renderer();
  if (!r) return;
  if (!Array.isArray(r.events)) r.events = [];
  const event = { type, at: performance.timeOrigin + performance.now(), ...detail };
  r.events.push(event);
  window.dispatchEvent(new window.CustomEvent('lw:film', { detail: event }));
}

/** A DOMRect as the stream carries it: centre and size, CSS px; null for none. */
export function box(rect) {
  if (!rect) return null;
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    w: rect.width,
    h: rect.height,
  };
}

/** What an autoplay operation does, as the stream names it. */
export function actionKind(op) {
  if (op === 'type' || op === 'set') return 'type';
  if (op === 'choose' || op === 'pick' || op === 'menu') return 'select';
  return 'click';
}

/**
 * The ids a step captured, as the stream's `entity`: `loadId: 4812` becomes
 * `load: 4812`. Anything that is not an id (a PO the trainee typed) is left out.
 */
export function entityOf(captured) {
  const entity = {};
  for (const [key, value] of Object.entries(captured || {})) {
    const m = /^([a-z][A-Za-z]*)Id$/.exec(key);
    if (m && (typeof value === 'number' || /^[\w-]{1,40}$/.test(String(value)))) {
      entity[m[1]] = value;
    }
  }
  return Object.keys(entity).length ? entity : null;
}

/** After the browser has painted what the last change put on screen. */
export function afterPaint() {
  return new Promise(resolve => {
    if (typeof window.requestAnimationFrame !== 'function') return setTimeout(resolve, 0);
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
  });
}
